// src/react/useQueue.ts
import { useCallback, useEffect, useRef } from "react";

// src/common/helpers.ts
import { pathToRegexp } from "path-to-regexp";
function getCurrentUtcHour() {
  const now = /* @__PURE__ */ new Date();
  return now.getUTCHours();
}
function obfuscate(inputObj) {
  if (false)
    return JSON.stringify(inputObj);
  const inputStr = JSON.stringify(inputObj);
  const shift = getCurrentUtcHour();
  return inputStr.split("").map((char) => String.fromCharCode(char.charCodeAt(0) + shift)).join("");
}

// src/common/constants.ts
var METRONOME_VERSION = "8.5.2";
var METRONOME_REPORT_ROUTE = `/__metronome/${METRONOME_VERSION}/report`;
var METRONOME_WEB_VITALS_ROUTE = `/__metronome/${METRONOME_VERSION}/web-vitals.js`;

// src/react/useQueue.ts
if (typeof window !== "undefined") {
  window.__metronomeQueue = window.__metronomeQueue ?? [];
  window.__metronomeDoNotTrack = window.__metronomeDoNotTrack ?? false;
}
function useQueue() {
  const intervalId = useRef(void 0);
  const report = useCallback(() => {
    if (window.__metronomeQueue.length === 0 || window.__metronomeDoNotTrack) {
      return;
    }
    const str = obfuscate(window.__metronomeQueue);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(METRONOME_REPORT_ROUTE, str);
    } else {
      fetch(METRONOME_REPORT_ROUTE, { body: str, method: "POST", keepalive: true });
    }
    window.__metronomeQueue = [];
  }, []);
  useEffect(() => {
    const visibilityChangeHandler = () => {
      if (document.visibilityState === "hidden") {
        report();
      }
    };
    addEventListener("visibilitychange", visibilityChangeHandler);
    addEventListener("pagehide", report);
    addEventListener("beforeunload", report);
    intervalId.current = setInterval(report, 5e3);
    return () => {
      removeEventListener("visibilitychange", visibilityChangeHandler);
      removeEventListener("pagehide", report);
      removeEventListener("beforeunload", report);
      clearInterval(intervalId.current);
      report();
    };
  }, []);
  const enqueue = useCallback((event) => {
    window.__metronomeQueue.push(event);
  }, []);
  return { enqueue };
}

// src/react/useWebVitals.ts
import { useEffect as useEffect2, useState } from "react";

// src/react/useGetBrowserData.ts
import { useCallback as useCallback2 } from "react";
import { useLocation } from "@remix-run/react";
function useGetBrowserData() {
  const location = useLocation();
  const useGetBrowserData2 = useCallback2(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const minWidth = 768;
    const hasTouchSupport = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    let deviceCategory = "desktop";
    if (hasTouchSupport) {
      const hasSmallScreen = window.screen.width < minWidth;
      deviceCategory = hasSmallScreen ? "mobile" : "tablet";
    }
    return {
      pathname: location.pathname ?? "",
      url: window.location.href,
      hostname: window.location.hostname,
      referrer: document.referrer,
      screen: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      connection: connection?.effectiveType || "unknown",
      deviceCategory
    };
  }, [location]);
  return useGetBrowserData2;
}

// src/react/useWebVitals.ts
function useWebVitals(enqueue) {
  const getBrowserData = useGetBrowserData();
  const [webVitalScriptLoaded, setWebVitalScriptLoaded] = useState(false);
  useEffect2(() => {
    const handleOnLoad = () => {
      window._webVitals = window.webVitals;
      setWebVitalScriptLoaded(true);
    };
    const script = document.createElement("script");
    script.src = METRONOME_WEB_VITALS_ROUTE;
    script.onload = handleOnLoad;
    document.head.appendChild(script);
  }, []);
  useEffect2(() => {
    if (!webVitalScriptLoaded || typeof window._webVitals === "undefined")
      return;
    function enqueueWebVital(metric) {
      const webVitalMetric = {
        name: "web-vital",
        timestamp: Date.now(),
        metric: {
          id: metric.id,
          name: metric.name,
          value: metric.value,
          rating: metric.rating,
          navigationType: metric.navigationType
        },
        ...getBrowserData()
      };
      enqueue(webVitalMetric);
    }
    window._webVitals.onFCP(enqueueWebVital);
    window._webVitals.onLCP(enqueueWebVital);
    window._webVitals.onFID(enqueueWebVital);
    window._webVitals.onCLS(enqueueWebVital);
    window._webVitals.onTTFB(enqueueWebVital);
    window._webVitals.onINP(enqueueWebVital);
  }, [webVitalScriptLoaded]);
}

// src/react/useWebAnalytics.ts
import { useLocation as useLocation2 } from "@remix-run/react";
import { useEffect as useEffect3, useRef as useRef3 } from "react";
function useWebAnalytics(enqueue) {
  const lastLocationKey = useRef3();
  const location = useLocation2();
  const getBrowserData = useGetBrowserData();
  useEffect3(() => {
    const { key } = location;
    if (lastLocationKey.current === key)
      return;
    const pageviewMetric = {
      name: "pageview",
      timestamp: Date.now(),
      ...getBrowserData()
    };
    enqueue(pageviewMetric);
    lastLocationKey.current = key;
  }, [location, getBrowserData]);
}

// src/react/useClientErrors.ts
import { useEffect as useEffect4, useRef as useRef4 } from "react";
function useClientErrors(enqueue) {
  const getBrowserData = useGetBrowserData();
  const mounted = useRef4(false);
  useEffect4(() => {
    if (mounted.current)
      return;
    function eventHandler(event) {
      const { message, filename, lineno, colno, error } = event;
      const { stack } = error;
      enqueue({
        name: "client-error",
        timestamp: Date.now(),
        error: { name: error.name, message, filename, lineno, colno, stack },
        ...getBrowserData()
      });
    }
    window.addEventListener("error", eventHandler);
    mounted.current = true;
    return () => {
      window.removeEventListener("error", eventHandler);
    };
  }, [getBrowserData, enqueue]);
}

// src/react/MetronomeInstrumentation.tsx
var MetronomeInstrumentation = () => {
  const { enqueue } = useQueue();
  useWebVitals(enqueue);
  useWebAnalytics(enqueue);
  useClientErrors(enqueue);
  return null;
};
MetronomeInstrumentation.displayName = "MetronomeInstrumentation";

// src/react/withMetronome.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var withMetronome = process.env.NODE_ENV === "development" ? (App) => {
  return function Metronome(props) {
    return /* @__PURE__ */ jsx(App, { ...props });
  };
} : (App) => {
  return function Metronome(props) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(MetronomeInstrumentation, {}),
      /* @__PURE__ */ jsx(App, { ...props })
    ] });
  };
};

// src/react/useDoNotTrack.ts
import { useEffect as useEffect5 } from "react";
function useDoNotTrack(enable, options) {
  useEffect5(() => {
    if (enable) {
      window.__metronomeDoNotTrack = true;
    } else {
      window.__metronomeDoNotTrack = false;
    }
  }, [enable, options]);
}
export {
  useDoNotTrack,
  withMetronome
};
//# sourceMappingURL=react.js.map
